import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef, type ReactElement, type ReactNode } from 'react';

import { cn } from '@/lib/cn';

/**
 * Dialog — the modal surface.
 *
 * Scrim fades, panel scales in over --dur-normal/--ease-out. Radix traps focus, closes on
 * Esc and outside click, and marks the rest of the page inert. The body is the only scroll
 * region so the header and footer stay put.
 *
 * Focus return. Radix restores focus to the node that opened the dialog only when the
 * dialog was opened *through* `<DialogPrimitive.Trigger>` — i.e. only when this component
 * was given a `trigger`. A dialog driven by a controlled `open` prop has no trigger node to
 * return to, and focus lands on `<body>` when it closes. Those callers must hold a ref on
 * whatever opened the dialog and pass
 * `onCloseAutoFocus={(e) => { e.preventDefault(); ref.current?.focus(); }}`.
 */

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl';

/** Panel max-widths. Not tokens — dialog widths are per-component composition decisions. */
const SIZE_WIDTH: Record<DialogSize, number> = { sm: 400, md: 520, lg: 680, xl: 880 };

export const dialogOverlayClass = cn(
  'fixed inset-0 z-[var(--z-overlay)] bg-[var(--scrim)] backdrop-blur-[2px]',
  'data-[state=open]:animate-[af-fade-in_var(--dur-normal)_var(--ease-out)]',
  'data-[state=closed]:animate-[af-fade-out_var(--dur-fast)_var(--ease-out)]',
);

export const dialogPanelClass = cn(
  'fixed left-1/2 top-1/2 z-[var(--z-modal)] flex w-[calc(100vw-var(--space-8))] -translate-x-1/2 -translate-y-1/2',
  'max-h-[min(85dvh,760px)] flex-col overflow-hidden',
  'rounded-[var(--radius-xl)] border border-line bg-[var(--surface-1)] shadow-[var(--shadow-4)]',
  'focus:outline-none',
  'data-[state=open]:animate-[af-scale-in_var(--dur-normal)_var(--ease-out)]',
  'data-[state=closed]:animate-[af-fade-out_var(--dur-fast)_var(--ease-out)]',
);

export interface DialogProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Optional uncontrolled trigger. */
  trigger?: ReactElement;
  title: ReactNode;
  /** Hides the title visually but keeps it for assistive tech. */
  hideTitle?: boolean;
  description?: ReactNode;
  /** Small trailing slot in the header, left of the close button. */
  headerActions?: ReactNode;
  size?: DialogSize;
  footer?: ReactNode;
  children?: ReactNode;
  /** Removes the × control — only for flows that must be resolved by a footer action. */
  hideClose?: boolean;
  className?: string;
  /** Drops the body padding, for dialogs that own their own layout (e.g. a picker). */
  bare?: boolean;
  onOpenAutoFocus?: (event: Event) => void;
  onCloseAutoFocus?: (event: Event) => void;
  /** Blocks Esc / outside-click dismissal — pair with an explicit footer action. */
  dismissible?: boolean;
}

export function Dialog({
  open,
  defaultOpen,
  onOpenChange,
  trigger,
  title,
  hideTitle = false,
  description,
  headerActions,
  size = 'md',
  footer,
  children,
  hideClose = false,
  className,
  bare = false,
  onOpenAutoFocus,
  onCloseAutoFocus,
  dismissible = true,
}: DialogProps) {
  const blockDismiss = (event: Event) => {
    if (!dismissible) event.preventDefault();
  };

  return (
    <DialogPrimitive.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      {trigger ? <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger> : null}
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={dialogOverlayClass} />
        <DialogPrimitive.Content
          style={{ maxWidth: SIZE_WIDTH[size] }}
          className={cn(dialogPanelClass, className)}
          {...(description ? {} : { 'aria-describedby': undefined })}
          onOpenAutoFocus={onOpenAutoFocus}
          onCloseAutoFocus={onCloseAutoFocus}
          onEscapeKeyDown={blockDismiss}
          onPointerDownOutside={blockDismiss}
          onInteractOutside={blockDismiss}
        >
          {/* The × is taken out of the flow. In the flex row it stole its own
              width from the whole header column for the column's full height,
              so the title *and* the description wrapped 36px short of the
              measure every block below them runs to — a ragged right edge down
              one block of the dialog only. Out of flow, the reserve is a `pr-9`
              on the title alone, which is the only line the × can actually
              reach; the description wraps flush with the body. */}
          <header
            className={cn(
              'flex shrink-0 items-start gap-3 px-5 pt-4',
              description ? 'pb-3' : 'pb-4',
              'border-b border-line-subtle',
            )}
          >
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title
                className={cn(
                  'text-lg font-semibold leading-tight tracking-[var(--tracking-tight)] text-primary',
                  !hideClose && 'pr-9',
                  hideTitle && 'sr-only',
                )}
              >
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-1 text-base leading-snug text-muted">
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
            {headerActions ? (
              <div className={cn('flex shrink-0 items-center gap-1', !hideClose && 'mr-9')}>
                {headerActions}
              </div>
            ) : null}
            {hideClose ? null : (
              <DialogPrimitive.Close asChild>
                <button
                  type="button"
                  aria-label="Close dialog"
                  // Positioned against the panel (the only positioned ancestor),
                  // on the panel's own right gutter so it lines up with every
                  // other right edge in the dialog.
                  className={cn(
                    overlayCloseClass,
                    'absolute top-[var(--space-4)] right-[var(--space-5)]',
                  )}
                >
                  <X className="size-4" aria-hidden />
                </button>
              </DialogPrimitive.Close>
            )}
          </header>

          <div
            className={cn(
              'min-h-0 flex-1 overflow-y-auto overscroll-contain',
              bare ? '' : 'px-5 py-4',
            )}
          >
            {children}
          </div>

          {footer ? (
            <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-line-subtle bg-[var(--surface-2)] px-5 py-3">
              {footer}
            </footer>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** Shared × affordance for Dialog and Sheet — icon-only, always named. */
export const overlayCloseClass = cn(
  'flex size-[var(--h-control-sm)] shrink-0 items-center justify-center rounded-[var(--radius-sm)]',
  'text-muted transition-colors ease-[var(--ease-out)] duration-[var(--dur-fast)]',
  'hover:bg-[var(--surface-hover)] hover:text-primary',
  'active:bg-[var(--surface-active)]',
  'focus-visible:outline-2 focus-visible:outline-offset-1 outline-[color:var(--focus)]',
);

/** Left-aligned helper slot for a footer that also carries a hint or a destructive action. */
export const DialogFooterSpacer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(
  function DialogFooterSpacer({ className, ...rest }, ref) {
    return <div ref={ref} className={cn('mr-auto flex items-center gap-2', className)} {...rest} />;
  },
);

export const DialogRoot = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogOverlay = DialogPrimitive.Overlay;
/* Raw content, for the panels that are not this file's centred box — the nav
   drawer builds its own edge-anchored frame on it. */
export const DialogContent = DialogPrimitive.Content;
export const DialogTitle = DialogPrimitive.Title;
export const DialogDescription = DialogPrimitive.Description;
